import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Building2, ScanSearch, RefreshCw, Trash2, Pencil, FlaskConical, AlertTriangle, CheckCircle2, CircleDashed, Loader2, FileSpreadsheet } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import TenantFormDialog from "../../components/workspace/TenantFormDialog";
import ImportCsvDialog from "../../components/workspace/ImportCsvDialog";

const STATUS = {
  connected: { icon: CheckCircle2, label: "Connected", cls: "ok" },
  error: { icon: AlertTriangle, label: "Needs attention", cls: "warn" },
  unverified: { icon: CircleDashed, label: "Not verified", cls: "" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "never");

export default function TenantsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState(null);
  const [dialog, setDialog] = useState({ open: false, tenant: null });
  const [importOpen, setImportOpen] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => authApi.tenants.list().then(setTenants).catch((err) => toast.error("Could not load tenants", { description: errorMessage(err) })), []);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, fn, ok) => {
    setBusyId(id);
    try {
      const res = await fn();
      if (ok) toast.success(typeof ok === "function" ? ok(res) : ok);
      await load();
      return res;
    } catch (err) {
      toast.error("Action failed", { description: errorMessage(err) });
      return null;
    } finally {
      setBusyId("");
    }
  };

  const remove = (t) => {
    if (!window.confirm(`Remove "${t.name}" and all of its scans?`)) return;
    act(t.id, () => authApi.tenants.remove(t.id), "Tenant removed");
  };

  return (
    <div className="ws-page" data-testid="tenants-page">
      <header className="ws-head">
        <div>
          <h1>Microsoft tenants</h1>
          <p>Link your organization's Microsoft 365 tenants, scan them through Graph and plan the target license state.</p>
        </div>
        {isAdmin && (
          <div className="ws-head-actions">
            <button type="button" className="tb-btn" onClick={() => setImportOpen(true)} data-testid="import-csv-btn" title="Import a Microsoft 365 admin center export instead of connecting to Graph">
              <FileSpreadsheet size={15} /> Import CSV
            </button>
            <button type="button" className="tb-btn primary" onClick={() => setDialog({ open: true, tenant: null })} data-testid="add-tenant-btn">
              <Plus size={15} /> Link tenant
            </button>
          </div>
        )}
      </header>

      {tenants === null && <div className="ws-empty">Loading tenants...</div>}
      {tenants?.length === 0 && (
        <div className="ws-empty glass" data-testid="tenants-empty">
          <Building2 size={28} />
          <h3>No tenants linked yet</h3>
          <p>{isAdmin ? "Link a Microsoft tenant with an Entra app registration, or create a sample tenant to explore." : "Ask an admin to link a Microsoft tenant."}</p>
          {isAdmin && (
            <button type="button" className="tb-btn primary" onClick={() => setDialog({ open: true, tenant: null })} data-testid="add-tenant-empty-btn">
              <Plus size={15} /> Link your first tenant
            </button>
          )}
        </div>
      )}

      <div className="tenant-grid">
        {(tenants || []).map((t) => {
          const st = STATUS[t.status] || STATUS.unverified;
          const busy = busyId === t.id;
          return (
            <article key={t.id} className="tenant-card glass" data-testid={`tenant-card-${t.id}`}>
              <div className="tenant-top">
                <Link to={`/app/tenants/${t.id}`} className="tenant-name" data-testid="tenant-link">
                  {t.sample && <FlaskConical size={14} className="sample-ico" title="Sample data" />}
                  {t.source === "csv" && <FileSpreadsheet size={14} className="sample-ico" title="Imported from CSV" />}
                  {t.name}
                </Link>
                <span className={`status-pill ${st.cls}`} data-testid="tenant-status">
                  <st.icon size={12} /> {t.source === "csv" ? "CSV import" : st.label}
                </span>
              </div>
              <dl className="tenant-meta">
                <div>
                  <dt>Microsoft org</dt>
                  <dd>{t.org_display_name || "—"}</dd>
                </div>
                <div>
                  <dt>Domain</dt>
                  <dd>{t.default_domain || "—"}</dd>
                </div>
                <div>
                  <dt>Tenant ID</dt>
                  <dd className="mono">{t.sample ? "sample" : t.source === "csv" ? "csv import" : t.tenant_id}</dd>
                </div>
                <div>
                  <dt>{t.source === "csv" ? "Last import" : "Last scan"}</dt>
                  <dd>{fmtDate(t.last_scan_at)}</dd>
                </div>
              </dl>
              {t.status === "error" && t.last_error && (
                <div className="ws-alert warn" data-testid="tenant-error">
                  <AlertTriangle size={13} /> {t.last_error}
                </div>
              )}
              <div className="tenant-actions">
                <Link to={`/app/tenants/${t.id}`} className="tb-btn sm">
                  <ScanSearch size={13} /> Open
                </Link>
                {isAdmin && (
                  <>
                    {t.source !== "csv" && (
                      <button type="button" className="tb-btn sm" disabled={busy} onClick={() => act(t.id, () => authApi.tenants.verify(t.id), (r) => (r.ok ? "Connection OK" : `Connection failed: ${r.error}`))} data-testid="tenant-verify">
                        {busy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />} Test
                      </button>
                    )}
                    <button type="button" className="tb-btn sm" onClick={() => setDialog({ open: true, tenant: t })} data-testid="tenant-edit">
                      <Pencil size={13} /> Edit
                    </button>
                    <button type="button" className="tb-btn sm ghost danger" onClick={() => remove(t)} data-testid="tenant-delete">
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {dialog.open && <TenantFormDialog key={dialog.tenant?.id || "new"} open onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} tenant={dialog.tenant} onSaved={load} />}
      {importOpen && <ImportCsvDialog open onOpenChange={setImportOpen} tenants={tenants || []} onDone={(res) => navigate(`/app/tenants/${res.tenant_id}`)} />}
    </div>
  );
}
